import { useMemo } from 'react';
import { SessionTrendDataDto } from '@repo/shared/dtos/analytics/AnalyticsResponseDto';
import { ResponsiveLine } from '@nivo/line';
import { useResponsiveChartConfig } from '../shared/useResponsiveChartConfig';
import { LineChart } from 'lucide-react';
import { chartUtils } from '../utils/chartUtils';
import { dateUtils } from '../utils/dateUtils';
import { CHART_CONFIG, CHART_THEMES } from '../config';
import { MetricCard } from '../overview/MetricCard';
import { palette } from '../shared/theme';
import { ChartWrapper } from '../shared/ChartWrapper';

interface SessionTrendsChartProps {
  data: SessionTrendDataDto[];
  showMetric: 'sessions' | 'minutes';
  height?: number;
  isModalOpen?: boolean;
}

export function SessionTrendsChart({
  data,
  showMetric,
  height = CHART_CONFIG.MODAL_HEIGHT,
  isModalOpen = true,
}: SessionTrendsChartProps) {
  const { isMobile, isNarrow, getMargins } = useResponsiveChartConfig();
  const trendAnalysis = useMemo(() => chartUtils.analyzeTrends(data), [data]);

  const chartData = useMemo(() => {
    if (!data?.length) return null;
    return {
      lineData: chartUtils.prepareLineChartData(data, showMetric),
      analysis: trendAnalysis,
    };
  }, [data, showMetric, trendAnalysis]);

  if (!chartData) {
    return (
      <div className="flex h-full items-center justify-center text-gray-500">
        <p className="text-sm">No trend data available</p>
      </div>
    );
  }

  const { lineData, analysis } = chartData;
  const { bestMonth, worstMonth, totalGrowth } = analysis;

  return (
    <div className="space-y-6">
      <div className={`grid gap-3 ${isNarrow ? 'grid-cols-2' : 'grid-cols-4'}`}>
        <MetricCard
          value={`${totalGrowth >= 0 ? '+' : ''}${totalGrowth.toFixed(1)}%`}
          label="Overall Growth"
          accent={totalGrowth >= 0 ? CHART_CONFIG.COLORS.SUCCESS : CHART_CONFIG.COLORS.ERROR}
        />
        <MetricCard
          value={bestMonth?.sessionCount || 0}
          label="Peak Month Sessions"
          accent={palette.indigo}
        />
        <MetricCard
          value={bestMonth ? dateUtils.formatMonth(bestMonth.monthStart) : 'N/A'}
          label="Best Month"
          accent={'#e69b0d'}
        />
        <MetricCard
          value={worstMonth ? dateUtils.formatMonth(worstMonth.monthStart) : 'N/A'}
          label="Worst Month"
          accent={CHART_CONFIG.COLORS.ERROR}
        />
      </div>

      <div className={`rounded-lg border border-[var(--anl-border)] bg-[var(--anl-surface)] ${isMobile ? 'p-3' : 'p-6'}`}>
        <div className="flex items-center justify-between">
          <h3 className={`flex items-center gap-2 font-semibold text-[var(--anl-text)] ${isMobile ? 'text-sm' : 'text-lg'}`}>
            <LineChart className={`text-[var(--anl-text-muted)] ${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
            Percentage Change Over Time
          </h3>
        </div>
        <ChartWrapper height={isMobile ? 220 : height} className="mr-4 mt-4" isModalOpen={isModalOpen}>
          <ResponsiveLine
            data={lineData}
            margin={getMargins('MODAL')}
            xScale={{ type: 'point' }}
            yScale={{ type: 'linear', min: 'auto', max: 'auto', stacked: false }}
            curve="monotoneX"
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: -45,
              legend: 'Month',
              legendOffset: 60,
              legendPosition: 'middle',
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: 'Percentage Change (%)',
              legendOffset: -50,
              legendPosition: 'middle',
            }}
            pointSize={10}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={3}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabelYOffset={-12}
            useMesh={true}
            enableGridX={false}
            enableGridY={true}
            colors={[CHART_CONFIG.COLORS.PRIMARY, CHART_CONFIG.COLORS.SECONDARY]}
            markers={[
              {
                axis: 'y',
                value: 0,
                lineStyle: {
                  stroke: CHART_CONFIG.COLORS.NEUTRAL,
                  strokeWidth: 2,
                  strokeDasharray: '4 4',
                },
                legendOrientation: 'horizontal',
                legendPosition: 'top-right',
              },
            ]}
            theme={CHART_THEMES.default}
            tooltip={({ point }) => {
              const color = (point as any).serieColor || (point as any).seriesColor;
              const series = (point as any).serieId || (point as any).seriesId;
              const change = Number(point.data.y);
              const changeClass = change >= 0 ? 'text-green-500' : 'text-red-500';
              return (
                <div className="min-w-[220px] rounded-md border border-[var(--anl-border)] bg-[var(--anl-surface-elev)] px-3 py-2 text-[var(--anl-text)]">
                  <div className="text-xs text-[var(--anl-text-muted)]">
                    {(point.data as any).xFormatted || point.data.x}
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
                    <span className="text-xs capitalize text-[var(--anl-text-muted)]">
                      {series}
                    </span>
                    <span className="text-sm font-semibold">
                      {(point.data as any).yFormatted || point.data.y}
                    </span>
                  </div>
                  <div className={`mt-1 text-xs font-semibold ${changeClass}`}>
                    {change >= 0 ? '+' : ''}
                    {change.toFixed(1)}% vs prev. month
                  </div>
                </div>
              );
            }}
            animate={true}
            motionConfig="gentle"
          />
        </ChartWrapper>
      </div>
    </div>
  );
}
